import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild, ViewRef} from '@angular/core';
import {DiagnosticOrderActionsService} from '@ispm/test/services/diagnostic-order-actions.service';
import {DynamicModalLoaderService} from '../../../../layout/services/dynamic-modal-loader.service';
import {IDiagnosticOrder, IRevision} from '@ispm/test/interfaces/diagnostic-order.interface';
import {Observable, Subject, Subscription} from 'rxjs';
import {IDocumentTemplate} from '@ispm/test/components/documents-tab/interfaces/document-template.interface';
import {DocumentTestData} from '@ispm/test/components/documents-tab/interfaces/document-test-data.interface';
import {PlaceholderModalComponent} from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import {ModalTypes} from '@ispm/layout/components/modals/contants/modal-types.enum';
import {ReportSummaryTextComponent} from '@ispm/test/components/documents-tab/report-summary-text/report-summary-text.component';
import {ConfirmModalComponent} from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import {modalResponse} from '@ispm/layout/constants/modal-responses.enum';
import moment from 'moment';
import * as _ from 'lodash';
import {RelatedControlTest} from '../../../../quality-check/components/qc-results/qc-results.component';
import {QcService} from '../../../../quality-check/services/qc.service';
import {DocumentsTabService} from '@ispm/test/services/documents-tab.service';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {PdfJsViewerComponent} from 'ng2-pdfjs-viewer';
import {PdfService} from '@ispm/test/services/pdf.service';
import {TherapiesActionCreatorService} from "@ispm/therapies/services/therapies-action-creator.service";
import {debounceTime, startWith, switchMap, take, takeUntil, tap} from "rxjs/operators";
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import { TestService } from '../../services/test.service';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { ActivatedRoute } from "@angular/router";
import { RouterManagerService } from "@ispm/core/services/router-manager.service";
import {of} from "rxjs/observable/of";

const TEST_COMPLETED_STATUS = 'done';
const ADD_INTERPRETATION_SUMMARY = 'Enter interpretation summary';
const RELEASE_TEXT = 'Release';
const CANCEL_TEXT = 'Cancel';
const OK_TEXT = 'OK';
const FINAL_REPORT_TYPE = 'final';
const ADDENDUM_REPORT_TYPE = 'addendum';
const DOCUMENT_TYPE = "documentType";
const excludeQcandNavicanTestNames:Array<string> = ["Lung Screening","MDT","Lung Incidental"]

@Component({
  selector: 'gen-documents-tab',
  templateUrl: './documents-tab.component.html',
  styleUrls: ['./documents-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentsTabComponent implements OnInit {

  @ViewChild('pdfViewer', {static: false}) pdfViewer: PdfJsViewerComponent;

  public editModeChangeSubject:Subject<any> = new Subject();

  public showReleaseDocumentsMenu: boolean = false;
  public showEditDocumentsButton: boolean = true;
  public enableEditButton: boolean = true;
  public enableFreeStyleEdit: boolean = false;
  public testData: DocumentTestData = new DocumentTestData();
  public testRelatedDocuments: IDocumentTemplate[] = [];
  public currentDocument: IDocumentTemplate;
  public testIsCompleted: boolean = false;
  public editingIsPermitted: boolean = false;
  public postFinalReportToEmrEnabled: boolean = false;
  public postAddendumReportToEmrEnabled: boolean = false;
  public enableGenerateAndDownloadButton: boolean = false;
  public generateAndDownloadButtonText: string = "Generate";
  public pdfData: any = undefined;
  public downloadInProgress = false;
  public verticalLeftContainerPatientView = false;

  private revisionId: string;
  private userId: string;
  private testConfiguration: any;
  private currentRevision: IRevision;
  private currentTest: IDiagnosticOrder;
  private destroyed$: Subject<any> = new Subject();
  private userRole: any;
  private reportSummaryText: string;
  private addendumText: string = '';
  private modalSubscriber$: Subscription;
  public finalReportExists: boolean = false;
  public reportTypeToRelease: string = FINAL_REPORT_TYPE;
  private controlSampleStatus: any;
  private storedRelatedDocuments = [];
  public showPrintButton: boolean = true;
  private workflowRevisionFinalStatus: string;
  public toggleAction: boolean = false;

  constructor(private diagnosticOrderActionsService: DiagnosticOrderActionsService,
              private testService : TestService,
              private authService: AuthenticationService,
              private workflowService: WorkflowService,
              private notificationMessageService: NotificationMessageService,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private dynamicModalLoader: DynamicModalLoaderService,
              private activatedRoute: ActivatedRoute,
              protected documentsTabService: DocumentsTabService,
              private qcService: QcService,
              private coreActions: CoreActionCreatorService,
              private pdfService: PdfService,
              private detector: ChangeDetectorRef,
              private therapiesAC: TherapiesActionCreatorService,
              private routerManagerService: RouterManagerService
  ) {
  }

  ngOnInit() {

    this.userId = this.authService.user.info.id;
    this.userRole = this.authService.user.role;

    this.testData.workflowId = this.routerManagerService.parameters?.testId;
    this.testData.revisionId = this.routerManagerService.parameters?.revisionId;
    this.testData.currentTest = this.workflowService.currentTest;
    this.testData.testNumber = this.workflowService.currentTest.identifiers[0].value;

    const revisions = this.workflowService.currentTest.workflowRequest.revisions;
    this.testData.firstRevisionId = revisions[0].id;
    const testConfiguration = this.testService.testConfiguration;
    if(!excludeQcandNavicanTestNames.includes(testConfiguration.name)) {
      // reviews state are not updated in previous step, retrigger getting reviews from API
      this.therapiesAC.setReviews(null);
      this.therapiesAC.getReviews().pipe(take(1)).subscribe();
    }

    if (testConfiguration) {
      this.showReleaseDocumentsMenu = _.get(testConfiguration, 'multipleDocuments.showReleaseDocumentsMenu', false);
      this.showEditDocumentsButton = _.get(testConfiguration, 'xHtmlReportTemplates[0].showEditDocumentsButton', true);
      this.enableEditButton = _.get(testConfiguration, 'enableEditButton', true);
      this.enableFreeStyleEdit = _.get(testConfiguration, 'enableFreeStyleEdit', false);
      this.testRelatedDocuments = [];
      this.showPrintButton = _.get(testConfiguration, 'showPrintButton', true);

      this.finalReportExists = false;

      if (testConfiguration.taskDispatchTemplates
        && testConfiguration.taskDispatchTemplates.length) {
        let effectiveTemplates = testConfiguration.taskDispatchTemplates
          .filter(e => e
            && e.type
            && e.type == "postReportToEMR"
            && e.reference
            && e.settings
            && e.settings.enabled);
        if (effectiveTemplates && effectiveTemplates.length > 0) {
          this.postFinalReportToEmrEnabled = effectiveTemplates.filter(e => e.settings.releaseFinal).length > 0;
          this.postAddendumReportToEmrEnabled = effectiveTemplates.filter(e => e.settings.addAddendum).length > 0;
        }
      }

      if (testConfiguration.xHtmlReportTemplates && testConfiguration.xHtmlReportTemplates.length) {
        const templatesSorted = testConfiguration.xHtmlReportTemplates.sort((doc1, doc2) => {
          return _.has(doc1, 'order') && _.has(doc2, 'order') ? (parseInt(doc1.order) >= parseInt(doc2.order) ? 1 : -1) : 1;
        });

        templatesSorted.forEach(doc => {

          if (doc.type === FINAL_REPORT_TYPE) {
            this.finalReportExists = true;
            this.reportTypeToRelease = FINAL_REPORT_TYPE;
          }
          let enableGenerateAndDownloadButton = false;
          let generateAndDownloadButtonText = "Generate";
          if (doc.enableGenerateAndDownloadButton) {
            enableGenerateAndDownloadButton = doc.enableGenerateAndDownloadButton;
          }
          if (doc.generateAndDownloadButtonText) {
            generateAndDownloadButtonText = doc.generateAndDownloadButtonText;
          }

          if (!doc.hasOwnProperty('snapshot') || !doc.snapshot) {
            this.testRelatedDocuments.push(
              {
                title: doc.title,
                type: doc.type,
                version: doc.version,
                reference: doc.reference,
                labels: doc.labels,
                data: doc.data,
                enableGenerateAndDownloadButton: enableGenerateAndDownloadButton,
                generateAndDownloadButtonText: generateAndDownloadButtonText,
                clientDateFormat: doc.clientDateFormat
              }
            );
          }
        });
      }
      this.testConfiguration = testConfiguration;
      this.workflowRevisionFinalStatus = _.get(this.testConfiguration, 'workflowRevisionFinalStatus', undefined);
      this.verticalLeftContainerPatientView = !!testConfiguration.VerticalLeftContainerPatientView;
    }

    this.diagnosticOrderActionsService.getState()
      .filter(state => !!state.order && Object.keys(state.order).length > 0)
      .map(state => state.order)
      .takeUntil(this.destroyed$.asObservable())
      .subscribe(order => {
        this.currentTest = order;
        this.currentRevision = order.workflowRequest.revisions.find(rev => rev.id === this.testData.revisionId);
        if ( !this.currentRevision) {
          return;
        }

        // find status of current revision
        if (!!this.workflowRevisionFinalStatus) {
          this.testIsCompleted = this.currentRevision.status === this.workflowRevisionFinalStatus;
        }
        else {
          this.testIsCompleted = order.workflowRequest.status.toLowerCase() === TEST_COMPLETED_STATUS;
        }

        // prepare common data of test for all documents
        this.testData.revisionName = this.currentRevision.name;
        this.testData.revisionStatus = this.currentRevision.status;
        this.editingIsPermitted = this.workflowService.currentTest.performer === this.authService.user.id;
      });


    const document$ = this.documentsTabService.loadDocumentSubject.pipe(
      startWith(this.testRelatedDocuments[0]),
      debounceTime(100),
      takeUntil(this.destroyed$.asObservable())
    ).publish();

    document$.subscribe(document => this.onSelectedDocument(document));

    (this.documentsTabService.getAllTestDocuments(this.testData.revisionId) as Observable<any>)
      .map(response => response.body)
      .subscribe(data => {
        this.updateDocuments(data);
        document$.connect();
      });
  }

  private updateDocuments(data){
    this.storedRelatedDocuments = [];
    data.forEach(item => {
      const context = item.context;
      const documentTypeContext = context ? context.find(c => c.resourceType === DOCUMENT_TYPE) : undefined;
      if (documentTypeContext && documentTypeContext.reference) {
        this.storedRelatedDocuments.push({type: documentTypeContext.reference, descr: item});
      }
    });
  }
  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelectedDocument($event) {
    if(!$event){
      return;
    }
    this.currentDocument = $event;
    if(this.currentDocument.enableGenerateAndDownloadButton){
      this.enableGenerateAndDownloadButton = this.currentDocument.enableGenerateAndDownloadButton;
    }
    if(this.currentDocument.generateAndDownloadButtonText){
      this.generateAndDownloadButtonText = this.currentDocument.generateAndDownloadButtonText;
    }
    if (this.testIsCompleted) {
       this.downloadAndShowPdf($event);
    }
    if (!(<ViewRef>this.detector).destroyed) {
      this.detector.detectChanges();
    }
  }
  onGenerateAndDownloadReport() {
    this.addAndDownloadReport();
  }
  onEditingMode($event) {
    this.editModeChangeSubject.next($event);
    this.detector.detectChanges();
  }

  onReleaseReport($event) {
    const reportType = $event;
    switch (reportType) {
      case FINAL_REPORT_TYPE:
        this.showWarningAndReleaseFinalReport();
        break;
      default:
        this.onReleaseAddendumReport();
        break;
    }
  }

  getReportTypeToRelease() {
    return this.reportTypeToRelease;
  }

  onGenerateDocument($event) {
    $event == null ?
      this.testRelatedDocuments.forEach(doc => this.generateDocument(doc))
      : this.generateDocument($event);
  }

  private addAndDownloadReport() {
    let generatePopupMessageTitle = 'Generate Letter';
    let generatePopupMessageContent = 'Are you sure you want to Generate Letter?';
    let disableDownloadAndPrintPdfPopup = false;
    if (this.testConfiguration && this.testConfiguration.xHtmlReportTemplates[0]) {
      if (this.testConfiguration.xHtmlReportTemplates[0].generatePopupMessageTitle) {
        generatePopupMessageTitle = this.testConfiguration.xHtmlReportTemplates[0].generatePopupMessageTitle;
      }
      if (this.testConfiguration.xHtmlReportTemplates[0].generatePopupMessageContent) {
        generatePopupMessageContent = this.testConfiguration.xHtmlReportTemplates[0].generatePopupMessageContent;
      }
      if (this.testConfiguration.xHtmlReportTemplates[0].disableDownloadAndPrintPdfPopup) {
        disableDownloadAndPrintPdfPopup = this.testConfiguration.xHtmlReportTemplates[0].disableDownloadAndPrintPdfPopup;
      }
    }
    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(ConfirmModalComponent,
      {
        title: generatePopupMessageTitle,
        content: generatePopupMessageContent,
        approveBtn: OK_TEXT,
        declineBtn: CANCEL_TEXT
      })
      .filter(response => response.payload)
      .map(response => response.payload)
      .subscribe((response: modalResponse) => {
        if (response == 1) {
          this.coreActions.setLoader(true);
          this.documentsTabService.generateFinalReport(this.userId, this.currentDocument, this.testData, "").subscribe((response) => {
            if (!response) {
              this.coreActions.setLoader(false);
              return;
            } else {
              this.documentsTabService.initReview(this.revisionId);
              this.documentsTabService.getAllTestDocuments(this.testData.revisionId)
                .map(response => response.body)
                .subscribe(data => {
                  if (disableDownloadAndPrintPdfPopup) {
                    this.notificationMessageService.success(this.notificationMessage.SUCCESSFULLY_REPORT_GENERATED);
                  } else {
                    this.updateDocuments(data);
                    this.downloadAndPrintPdf(this.currentDocument);
                  }
                  this.diagnosticOrderActionsService.setTriggerLetterGenerate(data);
                  this.coreActions.setLoader(false);
                });
            }
          });
          this.reportTypeToRelease = ADDENDUM_REPORT_TYPE;
        }
        this.modalSubscriber$.unsubscribe();
        this.modalSubscriber$ = null;
      });
  }

  private generateDocument(document: IDocumentTemplate): any {
     // this.documentsTabService.generateTestDocument(this.testData, document);
  }

  // --------------------- Addendum report related methods -------------
  private onReleaseAddendumReport() {
    const released = false;
    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(PlaceholderModalComponent, {
      title: ADD_INTERPRETATION_SUMMARY,
      componentToLoad: ReportSummaryTextComponent,
      buttons: {
        approveBtn: RELEASE_TEXT,
        declineBtn: CANCEL_TEXT
      },
      payload: {
        summaryText: this.addendumText
      },
      approveCallback: (summaryText) => {
        this.askAndReleaseAddendumReport();
        return Observable.of(true);
      },
    }, ModalTypes.NONE)
      .filter(response => response.payload)
      .map(response => response.payload)
      .subscribe(() => {
      });
  }

  private askAndReleaseAddendumReport() {
    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(ConfirmModalComponent,
      {
        title: 'Release Addendum Report',
        content: 'Are you sure you want to release addendum report?',
        approveBtn: OK_TEXT,
        declineBtn: CANCEL_TEXT
      })
      .filter(response => response.payload)
      .map(response => response.payload)
      .subscribe((response: modalResponse) => {
          if (response == 1) {

            const docTemplate = _.find(this.testRelatedDocuments, item => item.type == FINAL_REPORT_TYPE);
            if (docTemplate) {
              this.releaseAddendumReport(docTemplate);
            }
          }
          this.modalSubscriber$.unsubscribe();
          this.modalSubscriber$ = null;
        }
      );
  }

  private releaseAddendumReport(docTemplate: IDocumentTemplate) {
    const addendumObj = {
      updateText: this.addendumText,
      clientDate: moment().format('MM/DD/YY'),
      userId: this.userId,
      reportTemplatesExist: this.finalReportExists
    };

    this.testData.currentTest.localDateTime = this.testService.getLocalDateTime();
    this.testData.currentTest.localTime = this.testService.getLocalTime();
    this.documentsTabService.releaseAddendumReport(this.userId, docTemplate, this.testData, addendumObj).subscribe((pdfReport) => {
      if(pdfReport && this.postAddendumReportToEmrEnabled){
        this.documentsTabService.postReportToEmr(this.testData.workflowId,this.testData.revisionId).map((res) => {
          res && res.data ? this.notificationMessageService.success(this.notificationMessage.ADDENDUM_REPORT_WAS_SCHEDULED_FOR_POSTING_TO_EMR)
            : this.notificationMessageService.error(this.notificationMessage.FAILED_POSTING_ADDENDUM_REPORT_TO_EMR)
        }).catch((err) => {
          this.notificationMessageService.error(this.notificationMessage.FAILED_POSTING_ADDENDUM_REPORT_TO_EMR);
          return Observable.throw(err);
        })
      }
    });
  }

  // --------------------- Final report related methods -----------------
  private showWarningAndReleaseFinalReport() {
    const qcWarningConfig = this.testService.testConfiguration.qcWarningConfig;
    if (!qcWarningConfig) {
      this.onReleaseFinalReport();
      return true;
    } else {
      this.qcService.getRelatedQCTests(this.currentTest.workflowRequest.id, this.testService.testConfiguration.testType)
        .pipe(
          takeUntil(this.destroyed$.asObservable()),
          switchMap((res: RelatedControlTest[]) => {
            return this.qcService.loadControlSampleStatusesConfig()
              .pipe(
                tap((resStatus: RelatedControlTest[]) => {
                  this.controlSampleStatus = resStatus;

                  return of(res);
                })
              );
          })
        )
        .subscribe((relatedTests: RelatedControlTest[]) => {
            const showWarning = relatedTests && relatedTests.length > 0 ? this.analyseRelatedControlTestStatus(relatedTests[0]) : false;
            if (showWarning) {
              this.dynamicModalLoader.popupModal(ConfirmModalComponent,
                {
                  title: qcWarningConfig.title,
                  content: qcWarningConfig.content,
                  approveBtn: qcWarningConfig.approveBtn,
                  declineBtn: qcWarningConfig.declineBtn
                }, qcWarningConfig.dialogType).subscribe((response) => {
                if (response.payload === 1) {
                  this.onReleaseFinalReport();
                  return true;
                }
              });
            }
            else {
              this.onReleaseFinalReport();
              return true;
            }
          }
        );
    }
    return false;
  }

  private onReleaseFinalReport() {
    const released = false;
    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(PlaceholderModalComponent, {
      title: ADD_INTERPRETATION_SUMMARY,
      componentToLoad: ReportSummaryTextComponent,
      buttons: {
        approveBtn: RELEASE_TEXT,
        declineBtn: CANCEL_TEXT
      },
      payload: {
        summaryText: this.reportSummaryText
      },
      approveCallback: (summaryText) => {
        this.reportSummaryText = summaryText;
        this.askAndReleaseFinalReport(summaryText);
        return Observable.of(true);
      },
    }, ModalTypes.NONE)
      .filter(response => response.payload)
      .map(response => response.payload)
      .subscribe(() => {
      });
  }

  private askAndReleaseFinalReport(summaryText: string) {
    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(ConfirmModalComponent,
      {
        title: 'Release Final Report',
        content: 'Are you sure you want to release final report?',
        approveBtn: OK_TEXT,
        declineBtn: CANCEL_TEXT
      })
      .filter(response => response.payload)
      .map(response => response.payload)
      .subscribe((response: modalResponse) => {
        if (response == 1) {
          // get report template data, add interpretarion text and get userId
          const docTemplate = _.find(this.testRelatedDocuments, item => item.type == FINAL_REPORT_TYPE);
          if (docTemplate) {
            this.documentsTabService.releaseFinalReport(this.userId, docTemplate, this.testData, summaryText);
            // Important! In any test release of final report moves test to completed state

            this.coreActions.setLoader(true);
            this.workflowService.performWorkflowStep(this.currentTest, 'final report', '', this.currentRevision.id,
              this.currentTest.workflowRequest.id, summaryText,'','')
              .then(() => {
                if (this.postFinalReportToEmrEnabled) {
                  this.documentsTabService.postReportToEmr(
                    this.testData.workflowId,
                    this.testData.revisionId).map((res) => {
                    res && res.data ? this.notificationMessageService.success(this.notificationMessage.FINAL_REPORT_WAS_SCHEDULED_FOR_POSTING_TO_EMR)
                      : this.notificationMessageService.error(this.notificationMessage.FAILED_POSTING_FINAL_REPORT_TO_EMR)
                  }).catch((err) => {
                    this.notificationMessageService.error(this.notificationMessage.FAILED_POSTING_FINAL_REPORT_TO_EMR);
                    return Observable.throw(err);
                  })
                }
              }). then(() => {
                this.documentsTabService.initReview(this.revisionId);
                this.coreActions.setLoader(false);
              this.routerManagerService.reload();
            })
              .catch(() => {
                this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_SUBMIT_TEST);
                this.coreActions.setLoader(false);
              });
            this.reportTypeToRelease = ADDENDUM_REPORT_TYPE;
          }
        }
        this.modalSubscriber$.unsubscribe();
        this.modalSubscriber$ = null;
      });
  }

  analyseRelatedControlTestStatus(relatedTest: RelatedControlTest) {
    if (this.controlSampleStatus && relatedTest.orders && relatedTest.orders.length > 0) {
      return relatedTest.orders.some(order => order.resultStatus && order.resultStatus !== this.controlSampleStatus[0].display);
    }
    return false;
  }

  // --------------- Print document methods ----------------
  onPrintDocument($event) {
    $event == null ?
      this.testRelatedDocuments.forEach(doc => this.downloadAndPrintPdf(doc))
      : this.downloadAndPrintPdf($event);
  }

  downloadAndPrintPdf(document: IDocumentTemplate): any {
    const foundDescriptor = _.find(this.storedRelatedDocuments, (item) => item.type === document.type);
    if (foundDescriptor) {
      this.workflowService.getReportData(foundDescriptor.descr.id)
        .subscribe(result => {
          this.print(result.body);
        },err =>{
          this.notificationMessageService.error(this.notificationMessage.ERROR_WHILE_GETTING_DATA_FOR_PDF_FROM_SERVER);
        });
    }
  }

  downloadAndShowPdf(document: IDocumentTemplate): any {
    const foundDescriptor = _.find(this.storedRelatedDocuments, (item) => item.type === document.type);
    if (foundDescriptor) {
      this.downloadInProgress = true;
      this.workflowService.getReportData(foundDescriptor.descr.id)
        .subscribe(result => {
          const base64Data = this.pdfService.convertBase64ToBinary(result.body);
          this.pdfViewer.pdfSrc = new Blob([base64Data], { type: 'application/pdf'}); // pdfSrc can be Blob or Uint8Array
          this.downloadInProgress = false;
          this.detector.detectChanges();

          if (!(<ViewRef>this.detector).destroyed) {
            this.detector.detectChanges();
          }
          this.pdfViewer.refresh(); // Ask pdf viewer to load/reresh pdf

        },err=>{
          this.notificationMessageService.error(this.notificationMessage.ERROR_WHILE_GETTING_DATA_FOR_PDF_FROM_SERVER);
        });
    }
  }


  print(data: any): void {
    this.pdfService.printPDF(data);
  }

  toggleActionFromChild(toggleActionFromChild: boolean) {
    this.toggleAction = toggleActionFromChild;
  }
}
